import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import axiosInstance from "../../axios/axiosInstance";
import { AppDispatch, RootState } from "../store"; // Assuming you have a store setup

// Types
interface EmailPayload {
  name: string;
  email: string;
  mobile: string;
  desc: string;
}

interface EmailResponse {
  success: boolean;
  message?: string;
  data?: any;
}

interface EmailError {
  message: string;
  statusCode?: number;
}

interface EmailState {
  emailLoading: boolean;
  emailSuccess: boolean;
  emailError: EmailError | null;
}

const initialState: EmailState = {
  emailLoading: false,
  emailSuccess: false,
  emailError: null,
};

export const fetchEmail = createAsyncThunk<
  EmailResponse,
  EmailPayload,
  {
    rejectValue: EmailError;
    state: RootState;
    dispatch: AppDispatch;
  }
>(
  "email/fetchEmail",
  async ({ name, email, mobile, desc }, { rejectWithValue }) => {
    try {
      const payload = { name, email, mobile, desc };
      const response = await axiosInstance.post<EmailResponse>(
        `https://app.ceylonhospitals.com/api/v1/email/send`,
        payload
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue({
          message: error.response?.data?.message || error.message,
          statusCode: error.response?.status,
        });
      }
      return rejectWithValue({
        message: "An unknown error occurred while sending email",
      });
    }
  }
);

const emailSlice = createSlice({
  name: "email",
  initialState,
  reducers: {
    clearEmailSuccess: (state) => {
      state.emailSuccess = false;
    },
    clearEmailError: (state) => {
      state.emailError = null;
    },
    resetEmailState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmail.pending, (state) => {
        state.emailLoading = true;
        state.emailSuccess = false;
        state.emailError = null;
      })
      .addCase(fetchEmail.fulfilled, (state) => {
        state.emailLoading = false;
        state.emailSuccess = true;
      })
      .addCase(fetchEmail.rejected, (state, action) => {
        state.emailLoading = false;
        state.emailError = action.payload || {
          message: "Unknown error occurred",
        };
      });
  },
});

export const { clearEmailSuccess, clearEmailError, resetEmailState } =
  emailSlice.actions;



export default emailSlice.reducer;